<template>
    <div>
        <PageTitle
            :title="t('simpatizantes.alta_titulo', { abono: textoSimpatizante })"
            class="strech"
        />
        <b-container>
            <div class="col-11 mx-auto">
                <!-- Bloque introductorio, replica el patrón del flujo NO-SGA.
                 El texto "{simpatizante}" se inyecta literal desde el manager
                 (no se traduce). -->
                <div class="tw:mt-8 tw:mb-8">
                    <h2 class="m-0 h1 text-uppercase">{{ t('simpatizantes.completa_datos') }}</h2>
                    <div class="subtitle h3 light mb-3 mb-lg-5">{{ t('simpatizantes.alta_info', { simpatizante: textoSimpatizante }) }}</div>
                </div>

                <!-- Paso 1: datos del comprador + selección de precio. -->
                <DatosSimpatizanteSgaComponent
                    v-if="paso >= 1"
                    :deshabilitado="paso > 1"
                    :precios="precios"
                    :precios-disponibles="preciosDisponibles"
                    :precios-cerrados="preciosCerrados"
                    :loading="loading"
                    :error="error"
                    @continuar="onPaso1Continuar"
                    @cancelar="onCancelar"
                />

                <!-- Paso 2: resumen + descuentos + condiciones. -->
                <ResumenSimpatizanteSgaComponent
                    v-if="paso >= 2"
                    :deshabilitado="paso > 2"
                    :cart-id="cartId"
                    :precio-info="precioSeleccionadoInfo"
                    :descuentos-auto="descuentosAuto"
                    :cupon-verificado="cuponVerificado"
                    :terminos="terminos"
                    :loading="loading || applyingCupon || verifyingCupon"
                    :total-pagar="totalPagar"
                    :importe-con-descuento="importeSinGastos"
                    :applying="applyingCupon"
                    :error="error"
                    @continuar="onPaso2Continuar"
                    @atras="onAtras"
                    @verificar-cupon="onVerificarCupon"
                    @quitar-cupon="onQuitarCupon"
                    @condiciones-change="(v) => terminos = v"
                    @cancelar="onCancelar"
                />

                <!-- Paso 3: pago (Paycomet / formulario Redsys). -->
                <PagoSimpatizanteSgaComponent
                    v-if="paso === 3"
                    :session-id="sessionId"
                    :jet-id="jetId"
                    :error="error"
                    :formulario-pago="formularioPago"
                    @compra-finalizada="onCompraFinalizada"
                    @error="onErrorPago"
                />
            </div>
        </b-container>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';
    import { useAltaSimpatizanteSga } from '@/composables/front/sga/useAltaSimpatizanteSga';
    import store from '@/store';
    import DatosSimpatizanteSgaComponent from '@/modules/front/sga/membresia/components/DatosSimpatizanteSgaComponent.vue';
    import ResumenSimpatizanteSgaComponent from '@/modules/front/sga/membresia/components/ResumenSimpatizanteSgaComponent.vue';
    import PagoSimpatizanteSgaComponent from '@/modules/front/sga/membresia/components/PagoSimpatizanteSgaComponent.vue';
    import PageTitle from '@/components/frontend/shared/PageTitle';
    import { t } from '@/utils/i18n-helper';

    const {
        paso, cartId, sessionId, jetId, formularioPago, totalPrice,
        precios, preciosDisponibles, preciosCerrados, loading, error,
        descuentosAuto, cuponVerificado, applyingCupon, verifyingCupon, terminos, datosComprador,
        precioSeleccionadoInfo, totalPagar, importeSinGastos,
        fetchInfo, enviarPaso1, autoCargarDescuentos, verificarCupon,
        aplicarCupon, quitarCupon,
        guardarCondiciones, solicitarCobro, irAPaso, cancelarYReiniciar,
        eliminarCarrito,
    } = useAltaSimpatizanteSga();

    const teatro = computed(() => store.getters.getTeatro || {});
    const textoSimpatizante = computed(() => teatro.value.texto_simpatizante || '');

    // El alta SIEMPRE empieza en paso 1. No restauramos de localStorage.
    // onMounted sólo carga la lista de precios para que el paso 1 los tenga
    // disponibles inmediatamente.
    onMounted(() => {
        fetchInfo();
    });

    async function onPaso1Continuar(comprador, precioId) {
        try {
            await enviarPaso1(comprador, precioId);
            // El composable carga los descuentos automáticos y publica el
            // paso 2 solo cuando están confirmados.
        } catch (e) {
            // El composable ya setea `error` con el mensaje del backend.
        }
    }

    async function onPaso2Continuar() {
        if (loading.value) return; // guard: dos clics no lanzan dos cobros
        loading.value = true;
        error.value = null;
        try {
            await guardarCondiciones(terminos.value);
            // solicitarCobro lanza si el backend no devuelve jet/formulario:
            // sin él NO se avanza al paso 3 (pago vacío).
            await solicitarCobro();
            irAPaso(3);
        } catch (e) {
            error.value = e?.response?.data?.message
                || e?.message
                || t('front.sga.checkout.error_derechos');
        } finally {
            loading.value = false;
        }
    }

    // Los errores de cupón no deben propagarse como promesas rotas desde el
    // listener del evento: se pintan en el estado de error del paso 2.
    async function onAplicarCupon(discount) {
        try {
            await aplicarCupon(discount);
        } catch (e) {
            error.value = e?.response?.data?.message
                || e?.message
                || t('front.sga.checkout.error_derechos');
        }
    }

    async function onVerificarCupon(codigo) {
        try {
            const discount = await verificarCupon(codigo);
            if (!discount) {
                error.value = t('front.sga.checkout.cupon_no_valido');
                return;
            }
            // El cupón se aplica solo tras confirmar guardar-descuentos en el
            // backend (aplicarCupon) — verificarCupon solo lo valida.
            await aplicarCupon(discount);
        } catch (e) {
            error.value = e?.response?.data?.message
                || e?.message
                || t('front.sga.checkout.error_derechos');
        }
    }

    async function onQuitarCupon() {
        try {
            await quitarCupon();
        } catch (e) {
            error.value = e?.response?.data?.message
                || e?.message
                || t('front.sga.checkout.error_derechos');
        }
    }

    function onAtras() {
        // Al volver de paso 2 a 1, el carrito creado en el paso 1 queda
        // huérfano (el alta se reintentará desde cero). Lo borramos en el
        // backend y reseteamos el estado local a paso 1. Si el borrado
        // falla (red/5xx), se muestra el error y NO se resetea: el carrito
        // sigue activo y el usuario puede reintentar la cancelación.
        eliminarCarrito().catch(e => {
            error.value = e?.response?.data?.message
                || e?.message
                || t('front.sga.checkout.error_derechos');
        });
    }

    // Cancelación del wizard (pasos 1/2/3): igual que onAtras, el borrado
    // del carrito debe confirmarse antes de resetear; si falla, error visible.
    async function onCancelar() {
        try {
            await cancelarYReiniciar();
        } catch (e) {
            error.value = e?.response?.data?.message
                || e?.message
                || t('front.sga.checkout.error_derechos');
        }
    }

    function onCompraFinalizada() {
        // PagoPaycometComponent redirige via CompraFinalizada.vue en el padre.
    }

    function onErrorPago() {
        // PagoPaycometComponent muestra su propio toast.
    }
</script>